import { type FC } from "react";
import { TEXT } from "@/constants/text";
import type { ArrivalReportRow } from "@/types/reporting";
import { fmtDwell, integerFormatter } from "@/utils/tableFormatters";
import styles from "@/pages/CampaignOverviewPage/widgets/ArrivalWidget.module.css";

interface ArrivalWidgetProps {
  error: string | null;
  isLoading: boolean;
  rows: ArrivalReportRow[];
}

const ArrivalWidget: FC<ArrivalWidgetProps> = ({ error, isLoading, rows }) => {
  const {
    dwellDescription,
    dwellEyebrow,
    errorBody,
    errorHeading,
    noData,
    title,
    uniqueVisitsDescription,
    uniqueVisitsEyebrow,
    visitsDescription,
    visitsEyebrow,
  } = TEXT.campaignOverview.arrival;

  if (isLoading) {
    return (
      <div className={styles.sectionCard}>
        <span className={styles.sectionLabel}>{title}</span>
        <div className={styles.metricsRow}>
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.metricCard}>
              <div className={styles.shimmerEyebrow} />
              <div className={styles.shimmerValue} />
              <div className={styles.shimmerDescription} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={[styles.sectionCard, styles.sectionCardError].join(" ")}>
        <span className={styles.sectionLabel}>{title}</span>
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>
            <svg
              fill="none"
              height="22"
              stroke="var(--color-orange)"
              strokeLinecap="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="22"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" x2="12" y1="8" y2="12" />
              <line x1="12" x2="12.01" y1="16" y2="16" />
            </svg>
          </div>
          <p className={styles.errorHeading}>{errorHeading}</p>
          <p className={styles.errorBody}>{errorBody}</p>
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={styles.sectionCard}>
        <span className={styles.sectionLabel}>{title}</span>
        <div className={styles.emptyState}>
          <p className={styles.emptyBody}>{noData}</p>
        </div>
      </div>
    );
  }

  // Sum visits and unique visits across rows (rollup:"all" may return one row
  // per campaign_id when multiple campaigns are selected).
  // Avg dwell time is a per-row average from the API — reweight by visits so
  // the KPI card shows a true campaign-level average, not a row-count average.
  const totalVisits = rows.reduce(
    (acc, row) => acc + (row.projected_visits ?? 0),
    0
  );
  const totalUniqueVisits = rows.reduce(
    (acc, row) => acc + (row.projected_unique_visits ?? 0),
    0
  );
  const dwellRows = rows.filter((row) => row.avg_dwell_time_min != null);
  const dwellVisits = dwellRows.reduce(
    (acc, row) => acc + (row.projected_visits ?? 0),
    0
  );
  const avgDwellTime =
    dwellVisits > 0
      ? dwellRows.reduce(
          (acc, row) =>
            acc + row.avg_dwell_time_min! * (row.projected_visits ?? 0),
          0
        ) / dwellVisits
      : null;

  return (
    <div className={styles.stack}>
      <span className={styles.sectionLabel}>{title}</span>
      <div className={styles.metricsRow}>
        <div className={styles.metricCard}>
          <span className={styles.eyebrow}>{visitsEyebrow}</span>
          <span className={styles.primaryValue}>
            {integerFormatter.format(totalVisits)}
          </span>
          <span className={styles.metricDescription}>{visitsDescription}</span>
        </div>
        <div className={styles.metricCard}>
          <span className={styles.eyebrow}>{uniqueVisitsEyebrow}</span>
          <span className={styles.secondaryValue}>
            {integerFormatter.format(totalUniqueVisits)}
          </span>
          <span className={styles.metricDescription}>
            {uniqueVisitsDescription}
          </span>
        </div>
        <div className={styles.metricCard}>
          <span className={styles.eyebrow}>{dwellEyebrow}</span>
          <span className={styles.secondaryValue}>
            {fmtDwell(avgDwellTime)}
          </span>
          <span className={styles.metricDescription}>{dwellDescription}</span>
        </div>
      </div>
    </div>
  );
};

export default ArrivalWidget;
